import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { onAuthStateChanged, type User as FirebaseUser } from 'firebase/auth'
import { ChevronDown, ChevronRight, Gift, Sparkles, Stamp, UserRound, UserRoundCheck } from 'lucide-react'
import { useI18n } from '../../i18n/useI18n'
import type { StampBooth, StampEventInfoProgram } from '../../services/api'
import { auth } from '../../lib/firebase'
import { useStore } from '../../store/useStore'
import { useStampRally } from '../../store/useStampRally'
import { getGuestId, getOrCreateGuestId } from '../../lib/guestId'

/**
 * イベント詳細 (イベント = 親) と企画詳細で共通の部品 (#1898)。
 *
 * 企画詳細はもともとイベント詳細の中身そのもの (説明・景品・参加方法・出店店舗) だった。
 * 2 つのページで書き分けると片方だけ直して食い違うので、ここにまとめる。
 */

/** 最初に並べるブースの件数。読み物のページなので、続きは畳んでおく。 */
const VISIBLE_BOOTHS = 6

/**
 * 企画の見出し。TOP の企画の枠 (StampProgramPanel) と同じく、**種類 (小) と
 * 企画名 (大) を必ず両方出す** — 企画名が種類と同じ企画だけ形が変わらないように。
 */
export function ProgramTitle({
  program,
  large = false,
}: {
  program: StampEventInfoProgram
  /** 企画詳細ページの見出し (h1) として出す */
  large?: boolean
}) {
  const { t } = useI18n()
  const label =
    program.kind === 'qr'
      ? t('stampRally.programQr')
      : program.event.activityLabel ||
        (program.event.type === 'ar' ? t('stampRally.arTitle') : t('stampRally.programActivity'))
  const Icon = program.kind === 'qr' ? Stamp : Sparkles
  const tone = program.kind === 'qr' ? 'text-navy' : 'text-gold'
  return (
    <>
      <p className={`flex items-center gap-1.5 font-body text-xs font-medium ${tone}`}>
        <Icon size={14} />
        {label}
      </p>
      {large ? (
        <h1 className="mt-0.5 font-display text-2xl text-navy">{program.event.name}</h1>
      ) : (
        <p className="mt-0.5 font-display text-base text-navy">{program.event.name}</p>
      )}
    </>
  )
}

/** 企画の景品と参加抽選の残り。 */
export function PrizeLines({ prize, lottery }: Pick<StampEventInfoProgram, 'prize' | 'lottery'>) {
  const { t } = useI18n()
  if (!prize) return null
  return (
    <div className="mt-2 flex items-start gap-2">
      <Gift size={16} className="mt-0.5 shrink-0 text-gold" />
      <div className="min-w-0">
        <p className="font-body text-sm text-text-primary">{prize.name}</p>
        {/* #1870 **参加を決める前に、何がどれだけ残っているかを出す。**
          * 名前だけだと「まだもらえるのか」が分からず、会場に着いてから
          * 品切れを知ることになる。品切れは赤、残りわずかは金 — 判定の
          * 閾値はサーバが持っている (status)。色だけでなく太字にして、
          * 流し読みでも目に入るようにする (色だけだと色覚特性で伝わらない)。 */}
        <p
          className={`font-body text-[13px] ${prize.status === 'ok' ? '' : 'font-semibold'} ${
            prize.status === 'sold_out'
              ? 'text-yokoso-red'
              : prize.status === 'low'
                ? 'text-gold'
                : 'text-text-secondary'
          }`}
        >
          {prize.status === 'sold_out'
            ? t('stampRally.prizeSoldOut')
            : t('stampRally.prizeRemaining', {
                remaining: String(prize.remaining),
                total: String(prize.totalStock),
              })}
        </p>
        {/* #1871 参加抽選。**「早いもの順」とは書かない** — 完走報酬は
          * 先着だが抽選はランダムなので、同じ文言だと「急げば当たる」と
          * 誤解させる。**残数と当選率を 1 行に並べない** — 当選率の分母は
          * くじ全体の残りで、当たり本数とは別のもの。くじの枚数を出せば
          * 残りの当たり ÷ 残りのくじ が自分で確かめられる。 */}
        {lottery && (
          <p className="mt-0.5 font-body text-[13px] text-text-secondary">
            {lottery.remaining > 0
              ? t('stampRally.lotteryRemaining', {
                  remainingDraws: String(lottery.remainingDraws),
                  remaining: String(lottery.remaining),
                })
              : t('stampRally.lotterySoldOut')}
            {lottery.remaining > 0 && lottery.oddsPercent > 0 && (
              <span className="text-text-tertiary">
                {' · '}
                {lottery.oddsPercent >= 100
                  ? t('stampRally.lotteryOddsAll')
                  : t('stampRally.lotteryOdds', { odds: String(lottery.oddsPercent) })}
              </span>
            )}
          </p>
        )}
      </div>
    </div>
  )
}

/**
 * #1870 参加方法。**入口 QR の着地点**なので、会場に着く前でも「どう参加するか」を
 * 先に決められるようにする。ブースの QR は「そこに着いた人」が読むものなので、
 * この選択は持てない。
 */
export function JoinCard({
  regionId,
  focusId,
  returnTo,
  hasLottery,
}: {
  regionId?: string
  /** 「スタンプを集めにいく」で TOP に名指しする ID (イベントでも企画でもよい) */
  focusId: string
  /** アカウントでログインした後に戻ってくるページ */
  returnTo: string
  /** 抽選のある企画があるか (ゲストは対象外と先に言う) */
  hasLottery: boolean
}) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const isLoggedIn = useStore((s) => s.isLoggedIn)
  const setManualRegion = useStampRally((s) => s.setManualRegion)
  // **API が「アカウント」として扱うかどうかで判定する。** apiFetch は Firebase の
  // ID トークンが取れれば Bearer を付ける (取れない時だけ X-Guest-Id)。store の
  // isLoggedIn だけを見ると、Firebase のセッションはあるが store が復元前、という
  // 状態で「ゲストで参加」を出してしまい、押しても実際にはアカウントとして
  // 通信する — 画面と実体がズレる。
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null)
  const [authResolved, setAuthResolved] = useState(false)
  useEffect(() => onAuthStateChanged(auth, (user) => {
    setFirebaseUser(user)
    setAuthResolved(true)
  }), [])
  const hasAccount = isLoggedIn || firebaseUser !== null
  // #1870 ゲスト ID は #1398 のものをそのまま使う (端末生成の 128bit 乱数)。
  // **「参加した」= ID を持っている**なので、発行済みかどうかで状態を出し分ける。
  // 単に見に来ただけの人に ID を配らないため、発行は「ゲストで参加」を押した時だけ。
  const [guestId, setGuestId] = useState<string | null>(() => getGuestId())

  if (!authResolved) return null

  if (hasAccount || guestId) {
    return (
      <div className="rounded-2xl bg-white px-4 py-4 shadow-card">
        <div className="flex items-center gap-2">
          {hasAccount ? (
            <UserRoundCheck size={18} className="shrink-0 text-matcha" />
          ) : (
            <UserRound size={18} className="shrink-0 text-text-tertiary" />
          )}
          <p className="font-body text-sm text-text-primary">
            {hasAccount ? t('stampRally.joinedAsAccount') : t('stampRally.joinedAsGuest')}
          </p>
        </div>
        <button
          onClick={() => {
            // #1870 **QR で来た人に場所とイベントを選び直させない。**
            // 地域はこのイベントのものを先に確定させ (ラリー画面が現在地を
            // 聞かずに済む)、`?event=` でこのイベントだけに絞る。
            if (regionId) void setManualRegion(regionId)
            navigate(`/stamp-rally?event=${encodeURIComponent(focusId)}`)
          }}
          className="tap-effect mt-3 h-12 w-full rounded-xl bg-navy font-display text-sm font-semibold text-white"
        >
          {t('stampRally.joinStart')}
        </button>
      </div>
    )
  }

  return (
    <div className="rounded-2xl bg-white px-4 py-4 shadow-card">
      <p className="font-display text-sm font-semibold text-text-primary">
        {t('stampRally.joinTitle')}
      </p>
      <button
        onClick={() => {
          // アカウントは既存のログインへ。戻り先を覚えさせてこのページに帰す
          sessionStorage.setItem('postLoginRedirect', returnTo)
          navigate('/login')
        }}
        className="tap-effect mt-3 w-full rounded-xl bg-navy px-4 py-3 text-left text-white"
      >
        <span className="font-display text-sm font-semibold">{t('stampRally.joinAccount')}</span>
        <span className="mt-0.5 block font-body text-[11px] opacity-90">
          {t('stampRally.joinAccountNote')}
        </span>
      </button>
      <button
        onClick={() => setGuestId(getOrCreateGuestId())}
        className="tap-effect mt-2 w-full rounded-xl border border-gray-300 px-4 py-3 text-left"
      >
        <span className="font-display text-sm font-semibold text-text-primary">
          {t('stampRally.joinGuest')}
        </span>
        <span className="mt-0.5 block font-body text-[11px] text-text-secondary">
          {t('stampRally.joinGuestNote')}
        </span>
        {/* #1870 **抽選のある企画があれば、選ぶ前にここで言う。**
          * 抽選はアカウント登録への対価なのでゲストは対象外だが、
          * 全ブース回った後に知らされるのがいちばん揉める。
          * 抽選を設定していないイベントでは出さない（嘘になる）。 */}
        {hasLottery && (
          <span className="mt-1 block font-body text-[11px] text-yokoso-red">
            {t('stampRally.joinGuestNoLottery')}
          </span>
        )}
      </button>
    </div>
  )
}

/**
 * #1886 参加ブースの紹介。「参加店 N 店」という数だけでは、客も営業先も中身が分からない。
 *
 * **続きもこのページで開く。** 当初はブース一覧 (/stamp-rally/stores) へ送っていたが、
 * 一覧もブース詳細も TOP 経由で取った context をメモリから読む作りで、context を
 * 持たないこのページから飛ぶと TOP へ戻される。
 */
export function BoothList({ stores }: { stores: StampBooth[] }) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const [showAll, setShowAll] = useState(false)
  if (stores.length === 0) return null
  const booths = showAll ? stores : stores.slice(0, VISIBLE_BOOTHS)
  return (
    <div id="booths" className="flex flex-col gap-2 scroll-mt-16">
      <h2 className="font-display text-base text-navy">{t('stampRally.eventInfoBooths')}</h2>
      {booths.map((booth) => (
        <button
          key={booth.id}
          // #1898 ブースは企画に属する。どの企画のブースかで引き直す
          onClick={() => navigate(`/stamp-rally/stores/${booth.id}?event=${booth.eventId ?? ''}`)}
          className="tap-card flex gap-3 rounded-2xl bg-white p-3 text-left shadow-card"
        >
          {booth.thumbnailUrl && (
            <img src={booth.thumbnailUrl} alt="" className="h-16 w-16 shrink-0 rounded-xl object-cover" />
          )}
          <div className="min-w-0 flex-1">
            <p className="font-body text-sm font-semibold text-text-primary">
              {booth.name}
              {/* #1886 営業先に実在の参加店だと誤解されると、あとで話がこじれる。
                  公開後はサーバが返さないので、この印が客に出ることはない。 */}
              {booth.isSample && (
                <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 align-middle text-[11px] font-normal text-amber-700">
                  {t('stampRally.boothSample')}
                </span>
              )}
            </p>
            {booth.description && (
              <p className="mt-0.5 line-clamp-2 font-body text-xs text-text-secondary">
                {booth.description}
              </p>
            )}
            {/* 特典は説明に埋もれさせない。営業でも客でも、ここが効く */}
            {booth.benefit && (
              <p className="mt-1 flex items-start gap-1 font-body text-xs text-gold">
                <Gift size={13} className="mt-0.5 shrink-0" />
                {/* #1886 運営が入れた改行をそのまま活かす */}
                <span className="whitespace-pre-wrap">{booth.benefit}</span>
              </p>
            )}
          </div>
          {/* 押せることが分かる印。カードのままだと**タップできると気付けない** */}
          <ChevronRight size={16} className="shrink-0 self-center text-text-tertiary" />
        </button>
      ))}
      {/* 読み物のページなので既定は 6 件まで。続きはその場で開く。 */}
      {!showAll && stores.length > VISIBLE_BOOTHS && (
        <button
          onClick={() => setShowAll(true)}
          className="tap-card flex items-center justify-between rounded-2xl bg-white px-4 py-3 shadow-card"
        >
          <span className="font-body text-sm text-text-primary">{t('stampRally.eventInfoBoothsMore')}</span>
          <ChevronDown size={16} className="shrink-0 text-text-tertiary" />
        </button>
      )}
    </div>
  )
}
